import { Result, Risky } from '@rotomeca/rop';
import { Optional } from '@rotomeca/utils';

/**
 * @Fire : Déclenche un événement personnalisé.
 * Utilise la méthode trigger() de BnumElement si elle existe (Fluent API),
 * sinon utilise le dispatchEvent standard.
 */
export function Fire(
  eventName: string,
  options: CustomEventInit = { bubbles: true, composed: true },
) {
  return function (originalMethod: any, context: ClassMethodDecoratorContext) {
    if (context.kind !== 'method') return;

    return function (this: any, ...args: any[]) {
      const result = originalMethod.apply(this, args);
      const detail = result ?? args[0];

      // Utilisation de trigger (BnumElement) ou dispatchEvent (Standard)
      if (typeof this.trigger === 'function') {
        this.trigger(eventName, detail, options);
      } else {
        this.dispatchEvent(new CustomEvent(eventName, { ...options, detail }));
      }

      return result;
    };
  };
}

/**
 * Interface pour les constructeurs d'événements.
 * Gère deux cas :
 * 1. La classe connaît son nom (new EventClass(options))
 * 2. Le nom est passé au décorateur (new EventClass(name, options))
 */
type EventConstructor<T extends CustomEvent> = new (...args: any[]) => T;

/**
 * @CustomFire : Décorateur de méthode (Stage 3).
 * Déclenche un événement d'une classe spécifique lors de l'appel de la méthode.
 * * @param EventClass La classe de l'événement à instancier (doit étendre CustomEvent).
 * @param eventName Optionnel : Force un nom d'événement spécifique.
 * @param options Options d'initialisation (bubbles, composed, etc.).
 */
export function CustomFire<T extends CustomEvent>(
  EventClass: EventConstructor<T>,
  eventName?: string,
  options: CustomEventInit = { bubbles: true, composed: true },
) {
  return function (originalMethod: any, context: ClassMethodDecoratorContext) {
    if (context.kind !== 'method') return;

    return function (this: any, ...args: any[]) {
      const result = originalMethod.apply(this, args);
      const detail = result ?? args[0];
      const eventInit = { ...options, detail };

      const event = _____StCustomFire
        .tryInitEvent(EventClass, eventName, eventInit)
        .unwrapOr(new EventClass(result));

      this.dispatchEvent(event);

      return result;
    };
  };
}

abstract class _____StCustomFire {
  @Risky()
  static tryInitEvent<T extends CustomEvent<any>>(
    EventClass: EventConstructor<T>,
    eventName: Optional<string>,
    eventInit: any,
  ): Result<T> {
    const event = eventName
      ? new EventClass(eventName, eventInit)
      : new EventClass(eventInit);
    return Result.Ok(event);
  }
}
